<template>
  <div>
    <h4 class="table-page-header">Compliance Dashboard</h4>

    <!-- Filters -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="resetAndFetch"
            @input="debouncedFetch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
        <AIVSelect v-model="filterOfficeId" :options="offices" placeholder="All Offices" @change="resetAndFetch" />
        <el-button
          type="primary"
          size="small"
          @click="showImportModal = true"
        >
          <i class="fa fa-cloud-upload-alt" style="margin-right: 4px"></i> Import from ADP
        </el-button>
        <el-button
          type="warning"
          size="small"
          :loading="regenerating"
          @click="regenerateMissingPdfs"
        >
          <i class="fa fa-file-pdf" style="margin-right: 4px"></i> Regenerate Missing PDFs
        </el-button>
      </div>
    </div>

    <!-- Legend -->
    <div class="compliance-legend">
      <span class="legend-item"><span class="legend-dot legend-dot--signed"></span> Signed</span>
      <span class="legend-item"><span class="legend-dot legend-dot--in_person"></span> In-Person</span>
      <span class="legend-item"><span class="legend-dot legend-dot--expiring"></span> Expiring</span>
      <span class="legend-item"><span class="legend-dot legend-dot--expired"></span> Expired</span>
      <span class="legend-item"><span class="legend-dot legend-dot--partially_signed"></span> Partially Signed</span>
      <span class="legend-item"><span class="legend-dot legend-dot--pending"></span> Pending</span>
      <span class="legend-item"><span class="legend-dot legend-dot--missing"></span> Missing</span>
      <span class="legend-item"><span class="legend-dot legend-dot--na"></span> N/A</span>
    </div>

    <!-- Matrix Table -->
    <AIVTable
      :columns="tableColumns"
      :data="matrix"
      :pagination="pagination"
      :item-name="'clients'"
      :loading="loading"
      @pageChange="handlePageChange"
      @pageSizeChange="handlePageSizeChange"
    >
      <template v-for="form in forms" v-slot:[`cell-${form.name}`]="{ row }">
        <span
          :key="form.id"
          class="compliance-dot"
          :class="`compliance-dot--${getFormStatus(row, form.name)}`"
          :title="getFormStatus(row, form.name)"
        ></span>
      </template>
      <template #cell-actions="{ row }">
        <el-button type="primary" size="mini" @click="openSendModal(row)">
          <i class="fa fa-paper-plane"></i>
        </el-button>
      </template>
    </AIVTable>

    <!-- Send Forms Modal -->
    <SendComplianceFormsModal
      :visible.sync="showSendModal"
      :clientId="selectedClient && selectedClient.id"
      :clientName="selectedClient && selectedClient.name"
      @sent="fetchDashboard"
    />

    <!-- Import ADP Compliance Modal -->
    <ImportAdpComplianceModal
      :visible.sync="showImportModal"
      @imported="fetchDashboard"
    />
  </div>
</template>

<script>
import { complianceFormsService } from "@/serviceHandlers/complianceFormsService";
import { officesService } from "@/serviceHandlers/officesService";
import SendComplianceFormsModal from "@/components/SendComplianceFormsModal.vue";
import ImportAdpComplianceModal from "@/components/ImportAdpComplianceModal.vue";

export default {
  name: "ComplianceDashboard",
  components: { SendComplianceFormsModal, ImportAdpComplianceModal },
  data() {
    return {
      forms: [],
      matrix: [],
      offices: [],
      filterOfficeId: null,
      searchQuery: "",
      pagination: {
        total: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: 20,
      },
      loading: false,
      showSendModal: false,
      showImportModal: false,
      selectedClient: null,
      debounceTimer: null,
      regenerating: false,
    };
  },
  computed: {
    tableColumns() {
      const cols = [
        { key: 'clientName', label: 'Client', sortable: false },
      ];
      for (const form of this.forms) {
        // Split multi-word display names onto two lines
        const label = form.displayName.includes(' ')
          ? form.displayName.replace(/ /, '<br>')
          : form.displayName;
        cols.push({ key: form.name, label, sortable: false });
      }
      cols.push({ key: 'actions', label: 'Actions', sortable: false });
      return cols;
    },
  },
  async mounted() {
    await this.fetchOffices();
    await this.fetchDashboard();
  },
  methods: {
    async fetchOffices() {
      try {
        this.offices = await officesService.list();
      } catch { /* ignore */ }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.fetchDashboard();
      }, 300);
    },
    resetAndFetch() {
      this.pagination.currentPage = 1;
      this.fetchDashboard();
    },
    async fetchDashboard() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.perPage,
        };
        if (this.filterOfficeId) params.officeId = this.filterOfficeId;
        if (this.searchQuery.trim()) params.search = this.searchQuery.trim();
        const result = await complianceFormsService.getDashboard(params);
        this.forms = result.forms || [];
        this.matrix = result.matrix || [];
        this.pagination.total = result.totalCount || 0;
        this.pagination.totalPages = Math.ceil(this.pagination.total / this.pagination.perPage) || 1;
      } catch (error) {
        this.$notifyError("Could not load compliance dashboard: " + error);
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.fetchDashboard();
    },
    handlePageSizeChange(size) {
      this.pagination.perPage = size;
      this.pagination.currentPage = 1;
      this.fetchDashboard();
    },
    getFormStatus(row, formName) {
      if (row.hasCustomConfig && !row.forms?.[formName]) {
        return "na";
      }
      return row.forms?.[formName]?.status || "missing";
    },
    openSendModal(row) {
      this.selectedClient = { id: row.clientId, name: row.clientName };
      this.showSendModal = true;
    },
    async regenerateMissingPdfs() {
      this.regenerating = true;
      try {
        const result = await complianceFormsService.regeneratePdfs();
        const successCount = result.success || 0;
        const failedCount = result.failed || 0;
        let msg = successCount > 0
          ? `Successfully regenerated ${successCount} PDF${successCount !== 1 ? 's' : ''}.`
          : 'No missing PDFs found to regenerate.';
        if (failedCount > 0) {
          const failedNames = (result.errors || []).map(e => e.formName).filter(Boolean).join(', ');
          msg += ` ${failedCount} failed${failedNames ? ': ' + failedNames : ''}.`;
        }
        this.$notify({ type: failedCount > 0 ? "warning" : "success", message: msg, title: "PDF Regeneration" });
        await this.fetchDashboard();
      } catch (error) {
        this.$notifyError("PDF regeneration failed: " + (error.message || error));
      } finally {
        this.regenerating = false;
      }
    },
  },
};
</script>

<style scoped>
.compliance-legend { display: flex; gap: 1.5rem; margin-bottom: 1rem; flex-wrap: wrap; }
.legend-item { display: flex; align-items: center; gap: 0.4rem; color: #e0e0e0; font-size: 0.85rem; }
.legend-dot { width: 12px; height: 12px; border-radius: 50%; display: inline-block; }
.legend-dot--signed { background: #67c23a; }
.legend-dot--in_person { background: #67c23a; border: 2px solid #4a9e2a; }
.legend-dot--expiring { background: #e6a23c; }
.legend-dot--expired { background: #f56c6c; }
.legend-dot--partially_signed { background: #409eff; }
.legend-dot--pending { background: #909399; }
.legend-dot--missing { background: #404040; border: 1px solid #606060; }
.legend-dot--na { background: transparent; border: 1px dashed #606060; }

.compliance-dot { width: 16px; height: 16px; border-radius: 50%; display: inline-block; cursor: default; }
.compliance-dot--signed { background: #67c23a; }
.compliance-dot--in_person { background: #67c23a; border: 2px solid #4a9e2a; }
.compliance-dot--expiring { background: #e6a23c; }
.compliance-dot--expired { background: #f56c6c; }
.compliance-dot--partially_signed { background: #409eff; }
.compliance-dot--pending { background: #909399; }
.compliance-dot--missing { background: #404040; border: 1px solid #606060; }
.compliance-dot--na { background: transparent; border: 1px dashed #606060; }

.filter-section { margin-bottom: 1rem; }
.filter-row { display: flex; gap: 1rem; align-items: center; flex-wrap: wrap; }
.filter-group.search-group { flex: 1; max-width: 400px; }
.search-input { width: 100%; }
</style>
